import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";

import { apiClient } from "../lib/apiClient";
import { ApiRequestError } from "../lib/errors";
import { DEFAULT_PAGE_SIZE, nextPageParam } from "../lib/pagination";
import { queryKeys } from "../lib/queryKeys";

const firstPageToken: string | undefined = undefined;

interface ArchivedAgentOptions {
  enabled: boolean;
  teamId?: string;
}

// Archive lifecycle data stays separate from active navigation data. Restore
// invalidates the resource root so both active and archived lists converge.
export function useArchivedAgents({ enabled, teamId }: ArchivedAgentOptions) {
  const queryClient = useQueryClient();
  const archivedAgents = useInfiniteQuery({
    enabled: enabled && teamId !== undefined,
    queryKey: queryKeys.agents.list({
      archived: "true",
      team_id: teamId,
    }),
    initialPageParam: firstPageToken,
    queryFn: async ({ pageParam }) => {
      const { data, error } = await apiClient.GET("/v1/agents", {
        params: {
          query: {
            archived: true,
            page_size: DEFAULT_PAGE_SIZE,
            page_token: pageParam,
            team_id: teamId,
          },
        },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
    getNextPageParam: nextPageParam,
  });
  const restoreAgent = useMutation({
    mutationFn: async (agentId: string) => {
      const { data, error } = await apiClient.POST("/v1/agents/{id}:restore", {
        params: { path: { id: agentId } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.agents.all,
      });
    },
  });

  return {
    hasNextPage: archivedAgents.hasNextPage,
    isError: archivedAgents.isError,
    isPending: archivedAgents.isPending && !archivedAgents.isError,
    loadMore: () => {
      void archivedAgents.fetchNextPage();
    },
    pages: archivedAgents.data?.pages,
    restore: (agentId: string) => {
      restoreAgent.mutate(agentId);
    },
    restoreError: restoreAgent.isError ? restoreAgent.error.message : null,
    restoringId: restoreAgent.isPending ? restoreAgent.variables : undefined,
  };
}
